import { useAttributeStore } from '@/store/attributeStore'

/** Friendly error messages that replace raw API messages for specific fields. */
const FRIENDLY_FIELD_MESSAGES: Record<string, string> = {
  vehicleTypeId: 'Please select a valid vehicle type.',
  claimReferenceNumber: "Claim Refernce Number is required"
}

/** Maps API payload field paths back to the form's { sectionId, fieldId } coordinates. */
const API_FIELD_TO_FORM: Record<string, { sectionId: string; fieldId: string }> = {
  claimReferenceNumber:            { sectionId: 'basic-information',  fieldId: 'claimRefNo' },
  typeOfLossId:                    { sectionId: 'basic-information',  fieldId: 'typeOfLoss' },
  dateOfLoss:                      { sectionId: 'basic-information',  fieldId: 'dateOfLoss' },
  dateClaimReceived:               { sectionId: 'basic-information',  fieldId: 'dateClaimReceived' },
  owner:                           { sectionId: 'basic-information',  fieldId: 'ownerId' },
  insured:                         { sectionId: 'basic-information',  fieldId: 'insured' },
  city:                            { sectionId: 'basic-information',  fieldId: 'contactCity' },
  state:                           { sectionId: 'basic-information',  fieldId: 'contactState' },
  valuationZipCode:                { sectionId: 'basic-information',  fieldId: 'valuationZipCode' },
  vehicleTypeId:                   { sectionId: 'basic-information',  fieldId: 'vehicleType' },
  'vehicle.vin':                   { sectionId: 'standard-equipment', fieldId: 'vin' },
  'vehicle.year':                  { sectionId: 'standard-equipment', fieldId: 'year' },
  'vehicle.makeId':                { sectionId: 'standard-equipment', fieldId: 'make' },
  'vehicle.modelId':               { sectionId: 'standard-equipment', fieldId: 'model' },
  'vehicle.numAxles':              { sectionId: 'standard-equipment', fieldId: 'noOfAxles' },
  'vehicle.brandedSalvageTitle':   { sectionId: 'standard-equipment', fieldId: 'branded' },
  'vehicle.truck.useTypeId':       { sectionId: 'standard-equipment', fieldId: 'use' },
  'vehicle.truck.gvwrId':          { sectionId: 'standard-equipment', fieldId: 'gvw' },
  'vehicle.truck.odometer':        { sectionId: 'standard-equipment', fieldId: 'odometer' },
  'vehicle.trailer.typeId':        { sectionId: 'standard-equipment', fieldId: 'type' },
  'vehicle.trailer.trailerTypeId': { sectionId: 'standard-equipment', fieldId: 'type' },
  'vehicle.trailer.length':        { sectionId: 'standard-equipment', fieldId: 'length' },
  'vehicle.truck.gps':             { sectionId: 'additional-options', fieldId: 'gps' },
  'vehicle.truck.airBrakes':       { sectionId: 'additional-options', fieldId: 'airBrakes' },

  // VVS submit-payload validation errors — keyed as `${vehicleType}.${section}.${vvsFieldName}`
  // to match the backend's vvsPayloadValidator/vvsFieldMapping field names verbatim (see
  // claim.service.ts sendToDownstream). Prefixed with vehicleType because a few VVS field names
  // (e.g. "Body", "Suspension") are reused by both TT and TR with different form fields.
  'TT.conditions.Interior':        { sectionId: 'vehicle-condition',  fieldId: 'interior' },
  'TT.conditions.Body':            { sectionId: 'vehicle-condition',  fieldId: 'body' },
  'TT.conditions.Glass':           { sectionId: 'vehicle-condition',  fieldId: 'interiorGlass' },
  'TT.conditions.Paint':           { sectionId: 'vehicle-condition',  fieldId: 'interiorPaint' },
  'TT.conditions.Engine':          { sectionId: 'vehicle-condition',  fieldId: 'drivetrainEngine' },
  'TT.conditions.Transmission':    { sectionId: 'vehicle-condition',  fieldId: 'drivetrainTransmission' },
  'TT.conditions.FWearRmg':        { sectionId: 'vehicle-condition',  fieldId: 'tiresFWearRmg' },
  'TT.conditions.RWearRmg':        { sectionId: 'vehicle-condition',  fieldId: 'tiresRWearRmg' },
  'TT.conditions.APU':             { sectionId: 'additional-options', fieldId: 'apu' },

  'TT.options.ConvSleeperSize':    { sectionId: 'additional-options', fieldId: 'convSleeperSize' },
  'TT.options.COESize':            { sectionId: 'additional-options', fieldId: 'coeSize' },
  'TT.options.InteriorTrim':       { sectionId: 'additional-options', fieldId: 'trimLevel' },
  'TT.options.GPS':                { sectionId: 'additional-options', fieldId: 'gps' },
  'TT.options.AirRideCab':         { sectionId: 'additional-options', fieldId: 'airRideCab' },
  'TT.options.AirConditioning':    { sectionId: 'additional-options', fieldId: 'airConditioning' },
  'TT.options.FrontWheelType':     { sectionId: 'additional-options', fieldId: 'frontWheelType' },
  'TT.options.OSRearWheelType':    { sectionId: 'additional-options', fieldId: 'osRearWheelType' },
  'TT.options.ISRearWheelType':    { sectionId: 'additional-options', fieldId: 'isRearWheelType' },
  'TT.options.FrontTireSize':      { sectionId: 'additional-options', fieldId: 'frontTireSize' },
  'TT.options.RearTireSize':       { sectionId: 'additional-options', fieldId: 'rearTireSize' },
  'TT.options.Suspension':         { sectionId: 'additional-options', fieldId: 'suspension' },
  'TT.options.LiftGateCap':        { sectionId: 'additional-options', fieldId: 'liftGateCapacity' },
  'TT.options.5WheelType':         { sectionId: 'additional-options', fieldId: 'fifthWheelType' },
  'TT.options.HitchType':          { sectionId: 'additional-options', fieldId: 'hitchType' },
  'TT.options.PTO':                { sectionId: 'additional-options', fieldId: 'pto' },
  'TT.options.WetLineKit':         { sectionId: 'additional-options', fieldId: 'wetLineKit' },
  'TT.options.Airbrakes':          { sectionId: 'additional-options', fieldId: 'airBrakes' },
  'TT.options.EngineBrake':        { sectionId: 'additional-options', fieldId: 'engineBrake' },
  'TT.options.SideFairing':        { sectionId: 'additional-options', fieldId: 'sideFairing' },
  'TT.options.WindDeflector':      { sectionId: 'additional-options', fieldId: 'windDeflector' },
  'TT.options.TankFairings':       { sectionId: 'additional-options', fieldId: 'tankFairings' },
  'TT.options.FullAero':           { sectionId: 'additional-options', fieldId: 'fullAerodynamics' },

  'TR.conditions.Lining':          { sectionId: 'vehicle-condition',  fieldId: 'interiorLining' },
  'TR.conditions.Body':            { sectionId: 'vehicle-condition',  fieldId: 'exteriorBody' },
  'TR.conditions.Paint':           { sectionId: 'vehicle-condition',  fieldId: 'exteriorPaint' },
  'TR.conditions.Hydraulic':       { sectionId: 'vehicle-condition',  fieldId: 'mechanicalHydraulics' },
  'TR.conditions.CoolUnit':        { sectionId: 'vehicle-condition',  fieldId: 'mechanicalCoolingUnit' },

  'TR.options.Height':             { sectionId: 'additional-options', fieldId: 'height' },
  'TR.options.Width':              { sectionId: 'additional-options', fieldId: 'width' },
  'TR.options.Suspension':         { sectionId: 'additional-options', fieldId: 'suspension' },
  'TR.options.TotalAxleCap':       { sectionId: 'additional-options', fieldId: 'totalAxleCapacity' },
  'TR.options.Abs':                { sectionId: 'additional-options', fieldId: 'abs' },
  'TR.options.Interior':           { sectionId: 'additional-options', fieldId: 'interiorLining' },
  'TR.options.RearDoor':           { sectionId: 'additional-options', fieldId: 'rearDoorType' },
  'TR.options.OSWheelType':        { sectionId: 'additional-options', fieldId: 'osWheelType' },
  'TR.options.ISWheelType':        { sectionId: 'additional-options', fieldId: 'isWheelType' },
  'TR.options.TireSize':           { sectionId: 'additional-options', fieldId: 'tireSize' },

  // VVS vinfields validation errors — vinfields values sit at the submit payload root (not
  // nested under conditions/options/usageData), keyed the same way: `${vehicleType}.vinFields.${vvsFieldName}`.
  'TT.vinFields.Make':             { sectionId: 'standard-equipment', fieldId: 'make' },
  'TT.vinFields.Model':            { sectionId: 'standard-equipment', fieldId: 'model' },
  'TT.vinFields.Style':            { sectionId: 'standard-equipment', fieldId: 'style' },
  'TT.vinFields.Type':             { sectionId: 'standard-equipment', fieldId: 'truck_type' },
  'TT.vinFields.Use':              { sectionId: 'standard-equipment', fieldId: 'use' },
  'TT.vinFields.EngineMfg':        { sectionId: 'standard-equipment', fieldId: 'engineMfg' },
  'TT.vinFields.EngineModel':      { sectionId: 'standard-equipment', fieldId: 'engineModel' },
  'TT.vinFields.EngineHp':         { sectionId: 'standard-equipment', fieldId: 'engineHP' },
  'TT.vinFields.TransMfg':         { sectionId: 'standard-equipment', fieldId: 'transMfg' },
  'TT.vinFields.Speeds':           { sectionId: 'standard-equipment', fieldId: 'noOfSpeeds' },
  'TT.vinFields.TransModel':       { sectionId: 'standard-equipment', fieldId: 'transModel' },
  'TT.vinFields.Axles':            { sectionId: 'standard-equipment', fieldId: 'noOfAxles' },
  'TT.vinFields.DriveAxles':       { sectionId: 'standard-equipment', fieldId: 'noOfDriveAxles' },
  'TT.vinFields.TagAxles':         { sectionId: 'standard-equipment', fieldId: 'noOfTagAxles' },
  'TT.vinFields.FrontAxleRating':  { sectionId: 'standard-equipment', fieldId: 'frontAxleRating' },
  'TT.vinFields.RearAxleRating':   { sectionId: 'standard-equipment', fieldId: 'rearAxleRating' },
  'TT.vinFields.Gvw':              { sectionId: 'standard-equipment', fieldId: 'gvw' },

  'TR.vinFields.Make':             { sectionId: 'standard-equipment', fieldId: 'make' },
  'TR.vinFields.Model':            { sectionId: 'standard-equipment', fieldId: 'model' },
  'TR.vinFields.Type':             { sectionId: 'standard-equipment', fieldId: 'type' },
  'TR.vinFields.Exterior':         { sectionId: 'standard-equipment', fieldId: 'exterior' },
  'TR.vinFields.Axles':            { sectionId: 'standard-equipment', fieldId: 'noOfAxles' },
  'TR.vinFields.Length':           { sectionId: 'standard-equipment', fieldId: 'length' },
  'TR.vinFields.Gvw':              { sectionId: 'additional-options', fieldId: 'gawrGvv' },
}

/** { [sectionId]: { [fieldId]: errorMessage } } */
export type ApiFieldErrors = Record<string, Record<string, string>>

/**
 * VVS's own field-name casing can vary slightly between what our master data actually stores and
 * what's hardcoded above (e.g. "Gvw" vs "GVW") — the backend already matches these case-
 * insensitively (see vvsPayloadValidator's validateRelocatedOptionsFields), so do the same here.
 * Falls back to a lowercase lookup only when the exact key isn't found, so no behavior changes
 * for the (overwhelmingly common) exact-match case.
 */
const API_FIELD_TO_FORM_LOWER: Record<string, { sectionId: string; fieldId: string }> = Object.fromEntries(
  Object.entries(API_FIELD_TO_FORM).map(([key, value]) => [key.toLowerCase(), value])
)

/** Matches `${vehicleType}.usageData.${vvsFieldName}` — Body Information's backend section. */
const USAGE_DATA_FIELD_PATTERN = /^(?:TT|TR)\.usageData\./i

/**
 * Body Information fields are dynamically generated per use/type from attribute_master, so
 * there's no static VVS-fieldName → form-fieldId table for them like conditions/options/vinFields
 * have. The backend bridges usageData field defs to attribute_master rows by label match (its
 * `displayName` <-> attribute_master.label — see vvsPayloadBuilder's buildUsageData), so this does
 * the same at runtime: look up the currently-cached attribute definitions (whichever
 * TRUCK_BODY_<USE>/TRAILER_BODY_<TYPE> category is loaded for the claim's use/type) for a label
 * match, and use its attribute_name as the form field id.
 */
function resolveUsageDataFieldId(displayName: string | undefined): string | undefined {
  if (!displayName) return undefined
  const needle = displayName.trim().toLowerCase()
  for (const attributes of Object.values(useAttributeStore.getState().cache)) {
    const match = attributes.find((a) => a.label.trim().toLowerCase() === needle)
    if (match) return match.attribute_name
  }
  return undefined
}

/**
 * Converts the API errors array into a nested map keyed by sectionId then fieldId.
 * Unmapped API fields are silently ignored.
 */
export function parseApiErrors(
  errors: Array<{ field: string; message: string; displayName?: string }>
): ApiFieldErrors {
  const result: ApiFieldErrors = {}
  for (const { field, message, displayName } of errors) {
    if (USAGE_DATA_FIELD_PATTERN.test(field)) {
      const fieldId = resolveUsageDataFieldId(displayName)
      if (!fieldId) continue
      result['body-information'] ??= {}
      result['body-information'][fieldId] = message
      continue
    }

    const target = API_FIELD_TO_FORM[field] ?? API_FIELD_TO_FORM_LOWER[field.toLowerCase()]
    if (!target) continue
    result[target.sectionId] ??= {}
    result[target.sectionId][target.fieldId] = FRIENDLY_FIELD_MESSAGES[field] ?? message
  }
  return result
}
